import { HttpResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { ListParameter } from '@models/common';
import { MyDriveFileModel } from '@models/files/mydrive-file.model';
import { ProjectFileModel } from '@models/files/project-file.model';
import { GenerateZipTimeEstimate } from '@models/files/zip-estimate.model';
import { ListResponse } from '@models/helpers';
import { FileChunkParams, FileItemModel, SearchFileModel } from '@models/module/fields/main';
import { MyDriveService } from '@module-instance/my-drive/common';
import { FileServiceType } from '@module-instance/my-drive/common/models/file-service.model';
import {
  AddChunkedVersionParams,
  CreateFolderParams,
  DeleteItemsModel,
  DeleteSharesParams,
  DuplicateVersionBody,
  DuplicateVersionParams,
  EditFolderParams,
  FolderParentModel,
  GetFolderParentsParams,
  GetUploadUrlParams,
  GetVersionUrlParams,
  MoveItemsModel,
  RenameFileParams,
  RestoreItemsModel,
  SaveFileInfoBody,
  SaveFileInfoParams,
  ShareFilesModel,
  ZipItemsModel,
} from '@module-instance/my-drive/common/models/request.models';
import { ProjectFileService } from '@module-instance/my-drive/common/services/project-files.service';
import { TableColumn } from '@shared/components/data-list/common';

export { FileServiceType };
export interface IFileService<T> {
  getTableColumns(): TableColumn[];

  getSearchColumns(isGlobal: boolean): TableColumn[];

  getRecycleBinColumns(): TableColumn[];

  getFileUrl(fileId: number, versionId: number, isPreview?: boolean, streaming?: boolean): Observable<string>;

  getFilesUrl(versionIds: number[]): Observable<
    {
      versionId: number;
      url: string;
    }[]
  >;

  getFile(fileId: number, parentId: number, entityInstanceId?: number, versionId?: number): Observable<T>;

  getFolderParents(params: GetFolderParentsParams): Observable<FolderParentModel[]>;

  searchFiles(props: SearchFileModel): Observable<ListResponse<FileItemModel>>;

  getFileUploadUrl(body: GetUploadUrlParams): string;

  getFileNewVersionUrl(body: GetVersionUrlParams): string;

  generateFileId(): Observable<string>;

  uploadChunk(fileId: string, formData: FormData): Observable<void>;

  saveFileInfo(params: SaveFileInfoParams, body: SaveFileInfoBody): Observable<FileItemModel>;

  addChunkedVersion(params: AddChunkedVersionParams, body: SaveFileInfoBody): Observable<FileItemModel>;

  renameFile(body: RenameFileParams): Observable<void>;

  createFolder(body: CreateFolderParams): Observable<void>;

  editFolder(body: EditFolderParams): Observable<void>;

  downloadFileFromUrl(url: string, filename?: string): void;

  getChunkData({ file, start, end, chunks }: FileChunkParams): FormData;

  duplicateVersion(params: DuplicateVersionParams, body: DuplicateVersionBody): Observable<number>;

  deleteSharesGlobal(body: ListParameter, parentId: number): Observable<void>;

  deleteShares(body: DeleteSharesParams): Observable<void>;

  saveFile(body: Partial<ProjectFileModel> & Partial<MyDriveFileModel>, entityInstanceId?: number): Observable<void>;

  deleteItems(body: DeleteItemsModel, entityInstanceId?: number): Observable<void>;

  permanentlyDeleteItems(body: DeleteItemsModel, entityInstanceId?: number): Observable<void>;

  restoreItems(body: RestoreItemsModel, entityInstanceId?: number): Observable<void>;

  downloadZip(body: ZipItemsModel, entityInstanceId?: number): Observable<HttpResponse<Blob> | GenerateZipTimeEstimate>;

  moveItems(body: MoveItemsModel, entityInstanceId?: number): Observable<void>;

  shareFiles(body: ShareFilesModel, entityInstanceId?: number): Observable<void>;

  setFileAsUserAvatar(fileId: number): Observable<void>;

  setFileAsCompanyLogo(fileId: number): Observable<void>;
}

@Injectable({ providedIn: 'root' })
export class FileFactoryService {
  private myDriveService = inject(MyDriveService);
  private projectFilesService = inject(ProjectFileService);

  public getServiceType(type: FileServiceType): IFileService<ProjectFileModel | MyDriveFileModel> {
    switch (type) {
      case FileServiceType.MyDrive:
        return this.myDriveService as IFileService<MyDriveFileModel>;

      case FileServiceType.ProjectFiles:
        return this.projectFilesService as IFileService<ProjectFileModel>;
    }
  }
}
